import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ServiceSelector from "@/pages/agenda/components/ServiceSelector";
import BookingSummary from "@/pages/agenda/components/BookingSummary";
import BookingSuccess from "@/pages/agenda/components/BookingSuccess";
import DateStrip from "@/components/feature/DateStrip";
import TimeSlotPicker from "@/components/feature/TimeSlotPicker";
import {useServiceCatalog} from '@/hooks/useServiceCatalog';
import { useClient } from "@/hooks/useClient";
import { getUpcomingDays, toDateKey } from "@/utils/date";

export default function Agenda() {
  const services=useServiceCatalog();
  const { client, addAppointment } = useClient();
  const [searchParams] = useSearchParams();

  const days = useMemo(() => getUpcomingDays(14), []);
  const initialService = searchParams.get("servicio");

  const [serviceId, setServiceId] = useState(
    initialService && services.some((item) => item.id === initialService)
      ? initialService
      : services[0].id,
  );
  const [selectedDate, setSelectedDate] = useState<Date>(days[0]);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bookingError, setBookingError] = useState('');

  const dateKey = toDateKey(selectedDate);
  const service = services.find((item) => item.id === serviceId) ?? services[0];

  const handleSelectDate = (day: Date) => {
    if (saving) return;
    setSelectedDate(day);
    setSelectedSlot(null);
  };

  const handleConfirm = async () => {
    if (!selectedSlot || saving) return;
    setSaving(true);
    setBookingError('');
    try {
    await addAppointment({
      serviceId: service.id,
      serviceName: service.name,
      date: dateKey,
      slot: selectedSlot,
    });
    setConfirmed(true);
    } catch (error) {
      setBookingError(error instanceof Error ? error.message : 'No pudimos guardar tu cita.');
      setSelectedSlot(null);
      window.dispatchEvent(new Event('alea-agenda-changed'));
    } finally { setSaving(false); }
  };

  const handleReset = () => {
    setConfirmed(false);
    setSelectedSlot(null);
  };

  if (confirmed && selectedSlot) {
    return (
      <BookingSuccess
        serviceName={service.name}
        dateKey={dateKey}
        slot={selectedSlot}
        clientName={client?.name ?? ""}
        onReset={handleReset}
      />
    );
  }

  return (
    <div className="animate-fade-in">
      <header className="px-5 pb-4 pt-6">
        <p className="font-label text-[10px] font-bold uppercase tracking-[0.28em] text-accent-700">
          ALÉA Club
        </p>
        <h1 className="mt-1 font-heading text-xl font-extrabold text-foreground-950">
          Agenda tu cita
        </h1>
        <p className="mt-0.5 text-xs leading-relaxed text-foreground-600">
          Elige servicio, día y horario. Citas cada hora: 10:00, 11:00, 12:00, 13:00, 16:00, 17:00 y 18:00. Comida de 14:00 a 16:00.
        </p>
      </header>

      <section className="px-5">
        <h2 className="mb-3 font-heading text-sm font-bold text-foreground-950">
          1. Elige tu servicio
        </h2>
        <ServiceSelector services={services} selectedId={serviceId} onSelect={(id)=>{if(!saving)setServiceId(id);}} />
      </section>

      <section className="mt-6 px-5">
        <h2 className="mb-3 font-heading text-sm font-bold text-foreground-950">2. Elige el día</h2>
        <DateStrip days={days} selectedKey={dateKey} onSelect={handleSelectDate} />
      </section>

      <section className="mt-6 px-5">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="font-heading text-sm font-bold text-foreground-950">
            3. Elige el horario
          </h2>
          <span className="inline-flex items-center gap-1 font-label text-[11px] font-medium text-foreground-600">
            <span className="h-2 w-2 rounded-full bg-accent-500" />
            Disponible
          </span>
        </div>
        <TimeSlotPicker dateKey={dateKey} selected={selectedSlot} onSelect={(slot)=>{if(!saving)setSelectedSlot(slot);}} />
        {bookingError && <p role="alert" className="mt-3 text-sm text-red-700">{bookingError}</p>}
      </section>

      {selectedSlot ? (
        <section className="mt-6 px-5">
          <BookingSummary
            clientName={client?.name ?? ""}
            clientPhone={client?.phone ?? ""}
            serviceName={service.name}
            dateKey={dateKey}
            slot={selectedSlot}
            onConfirm={handleConfirm}
            saving={saving}
          />
        </section>
      ) : null}

      <p className="px-5 pb-4 pt-6 text-center text-[11px] leading-relaxed text-foreground-500">
        Las citas se confirman por WhatsApp según disponibilidad de la cabina.
      </p>
    </div>
  );
}
